"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState } from "react"

/** The 8 declarable conditions — mirrors contracts' HealthConditionSchema. */
const HEALTH_CONDITION_VALUES = [
  "pregnancy",
  "breastfeeding",
  "type2_diabetes",
  "hypertension",
  "kidney_disease",
  "celiac",
  "eating_disorder_history",
  "other",
] as const
type HealthConditionValue = (typeof HEALTH_CONDITION_VALUES)[number]
import type {
  HardConstraint,
  HardConstraintsResponse,
  ProfileResponse,
} from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { MoreSubpageHeader } from "@/components/layout/MoreSubpageHeader"
import { LoadingState, ErrorState } from "@/components/state"
import { ChipMultiSelect } from "@/components/onboarding/ChipMultiSelect"
import { FreeTextListOrNone } from "@/components/onboarding/FreeTextListOrNone"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/onboarding/Checkbox"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { PlanSyncBanner, usePlanSync } from "@/components/plan/PlanSyncBanner"
import { useI18n } from "@/i18n"
import { EU14, constraintChipLabel } from "@/components/constraints/constraintDisplay"
import { OptionGroup } from "@/components/onboarding/OptionGroup"
import type { LactoseLevel } from "@bitebalance/contracts"
import {
  activeLactose,
  answerClarification,
  freeTextIntolerances,
  guardIntoleranceText,
  mergeChipSelection,
  splitChipSelection,
  type ClarificationAnswer,
} from "@/lib/constraintClarification"
import { ApiError } from "@/api/client"

/** ADR-080: the refusals a save can get, in the user's words (anything else stays the generic message). */
const SAVE_ERROR_COPY: Record<string, string> = {
  INTOLERANCE_LEVEL_REQUIRED: "moreProfile.constraints.errorLevelRequired",
  INTOLERANCE_LEVEL_NOT_ALLOWED: "moreProfile.constraints.errorLevelNotAllowed",
  CLARIFICATION_UNRESOLVED: "moreProfile.constraints.errorClarificationUnresolved",
}

/**
 * /app/more/constraints — hard-constraint (allergens/intolerances/never-eat)
 * view + edit via the dedicated `PUT /v1/profile/hard-constraints`
 * confirmation flow (ui-ux-spec.md: "Hard constraints are edited only in
 * Profile safety controls"). Health conditions are shown read-only: the
 * T04a contract only exposes a write path for them during onboarding
 * step 13 (`PUT /v1/onboarding/13`) — there is no post-onboarding mutation
 * endpoint for `healthConditions` (see packages/contracts/src/schemas/profile.ts,
 * which deliberately excludes them from `ProfilePatchRequestSchema`).
 * Rather than inventing an endpoint, this is documented here and the
 * section stays read-only until a dedicated endpoint exists.
 */
export default function ConstraintsPage() {
  const api = useApiClient()
  const { t } = useI18n()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [constraints, setConstraints] = useState<HardConstraintsResponse | null>(null)
  const [state, setState] = useState<"loading" | "ready" | "error">("loading")
  const [editing, setEditing] = useState(false)
  const [allergenDraft, setAllergenDraft] = useState<string[] | "none">("none")
  const [neverEatDraft, setNeverEatDraft] = useState<string[] | "none">("none")
  // ADR-080: the structured lactose intolerance (with the level the user chose) and
  // free-text intolerances are shown and edited here too — never folded into allergies.
  const [lactoseDraft, setLactoseDraft] = useState<{
    selected: boolean
    level: LactoseLevel | null
  }>({
    selected: false,
    level: null,
  })
  const [intoleranceTextDraft, setIntoleranceTextDraft] = useState<string[] | "none">("none")
  const [lactoseTextHint, setLactoseTextHint] = useState(false)
  const [saveErrorCode, setSaveErrorCode] = useState<string | null>(null)
  // A legacy row's one-time question being answered (it goes through the same confirm dialog).
  const [clarifyChoice, setClarifyChoice] = useState<"allergy" | "intolerance" | null>(null)
  const [clarifyLevel, setClarifyLevel] = useState<LactoseLevel | null>(null)
  const [clarifyAnswer, setClarifyAnswer] = useState<ClarificationAnswer | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  function syncDrafts(hc: HardConstraintsResponse) {
    const allergenIds = hc.constraints.filter(c => c.type === "allergen").map(c => c.subjectId)
    const neverEatIds = hc.constraints.filter(c => c.type === "never_eat").map(c => c.subjectId)
    setAllergenDraft(allergenIds.length > 0 ? allergenIds : "none")
    setNeverEatDraft(neverEatIds.length > 0 ? neverEatIds : "none")
    const lactose = activeLactose(hc.constraints)
    setLactoseDraft({ selected: lactose !== null, level: lactose?.level ?? null })
    const texts = freeTextIntolerances(hc.constraints)
    setIntoleranceTextDraft(texts.length > 0 ? texts : "none")
  }
  // Health-condition editing (owner request 2026-08-28) — writes via the
  // canonical step-13 PUT; see the health card below for the safety mirrors.
  const [healthEditing, setHealthEditing] = useState(false)
  const [healthDraft, setHealthDraft] = useState<HealthConditionValue[]>([])
  const [healthOtherText, setHealthOtherText] = useState("")
  const [healthBfAck, setHealthBfAck] = useState(false)
  // Current medications (free text). Editable and REMOVABLE here: the step-13 PUT is a full replace, so what
  // the request carries is exactly what is stored — never a silent carry-over of what the user removed.
  const [healthMeds, setHealthMeds] = useState<string[]>([])
  const [healthSaveState, setHealthSaveState] = useState<"idle" | "saving" | "saved" | "error">(
    "idle"
  )

  function startHealthEdit() {
    if (!profile) return
    setHealthDraft(profile.healthConditions.map(c => c.condition))
    setHealthOtherText(profile.healthConditions.find(c => c.condition === "other")?.otherText ?? "")
    setHealthBfAck(false)
    setHealthMeds([...new Set(profile.healthConditions.flatMap(c => c.medications))])
    setHealthSaveState("idle")
    setHealthEditing(true)
  }

  function toggleHealthCondition(condition: HealthConditionValue) {
    setHealthDraft(prev =>
      prev.includes(condition) ? prev.filter(c => c !== condition) : [...prev, condition]
    )
  }

  const pregnancyLossBlocked =
    healthEditing && healthDraft.includes("pregnancy") && profile?.goalType === "loss"
  const breastfeedingLossAckNeeded =
    healthEditing &&
    healthDraft.includes("breastfeeding") &&
    profile?.goalType === "loss" &&
    !profile.healthConditions.some(c => c.condition === "breastfeeding")

  async function handleHealthSave() {
    if (!profile) return
    setHealthSaveState("saving")
    try {
      // The medications are what the user left in the list (removing one, or all, is stored); like the
      // onboarding step they only exist next to a declared condition — none left means none stored.
      // otherText only travels with an active 'other' condition.
      const medications = healthDraft.length === 0 ? [] : healthMeds
      await api.putOnboardingStep(13, {
        step: 13,
        answer: {
          conditions: healthDraft,
          otherText: healthDraft.includes("other") ? healthOtherText.trim() : null,
          medications,
          none: healthDraft.length === 0,
        },
      })
      const prevKcal = profile.calorieTarget.kcal
      const updated = await api.getProfile()
      setProfile(updated)
      setHealthSaveState("saved")
      setHealthEditing(false)
      // ADR-033: a health-condition change can move the target (ADR-030
      // lactation allowance) and always changes what a safe plan is —
      // rebuild now, announced.
      startPlanSync({ fromKcal: prevKcal, toKcal: updated.calorieTarget.kcal })
    } catch {
      setHealthSaveState("error")
    }
  }
  const [dialogAck, setDialogAck] = useState(false)
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle")
  const { state: planSync, start: startPlanSync } = usePlanSync()

  useEffect(() => {
    let cancelled = false
    Promise.all([api.getProfile(), api.getHardConstraints()])
      .then(([p, hc]) => {
        if (cancelled) return
        // See ProfilePage.tsx / useOnboardingFlow.ts's safeGetProfile: a
        // client.ts typing gap (outside this task's edit scope) types this
        // result as `Profile`, but the wire response is `ProfileResponse`.
        setProfile(p as unknown as ProfileResponse)
        setConstraints(hc)
        syncDrafts(hc)
        setState("ready")
      })
      .catch(() => {
        if (!cancelled) setState("error")
      })
    return () => {
      cancelled = true
    }
  }, [api])

  async function handleConfirm() {
    if (!constraints) return
    setSaveState("saving")
    try {
      const otherConstraints = constraints.constraints.filter(
        c => c.type !== "allergen" && c.type !== "never_eat" && c.type !== "intolerance"
      )
      // ADR-080: the structured lactose intolerance carries the level the user chose; free text carries none.
      const nextIntolerances: HardConstraint[] = [
        ...(lactoseDraft.selected && lactoseDraft.level
          ? [
              {
                type: "intolerance" as const,
                subjectId: "lactose",
                status: "active" as const,
                level: lactoseDraft.level,
              },
            ]
          : []),
        ...(intoleranceTextDraft === "none"
          ? []
          : intoleranceTextDraft.map(subjectId => ({
              type: "intolerance" as const,
              subjectId,
              status: "active" as const,
            }))),
      ]
      const nextAllergens: HardConstraint[] =
        allergenDraft === "none"
          ? []
          : allergenDraft.map(subjectId => ({
              type: "allergen" as const,
              subjectId,
              status: "active" as const,
            }))
      const nextNeverEat: HardConstraint[] =
        neverEatDraft === "none"
          ? []
          : neverEatDraft.map(subjectId => ({
              type: "never_eat" as const,
              subjectId,
              status: "active" as const,
            }))
      const updated = await api.putHardConstraints({
        baseRevision: constraints.revision,
        constraints: [...otherConstraints, ...nextAllergens, ...nextIntolerances, ...nextNeverEat],
        confirmed: true,
      })
      setConstraints(updated)
      syncDrafts(updated)
      setSaveErrorCode(null)
      setSaveState("saved")
      setConfirmOpen(false)
      setEditing(false)
      setDialogAck(false)
      // ADR-033 (truthfulness fix): the confirm dialog has always SAID the
      // plan regenerates after a hard-rule change — now it actually does,
      // with the announcing banner. A meal violating a new allergen must
      // never survive in the active plan.
      startPlanSync({
        fromKcal: null,
        toKcal: profile ? profile.calorieTarget.kcal : null,
      })
    } catch (err) {
      setSaveErrorCode(err instanceof ApiError ? err.envelope.code : null)
      setSaveState("error")
    }
  }

  /** ADR-080: answer ONE pending clarification through the same confirmed PUT (regen included). */
  async function submitClarification() {
    if (!constraints || !clarifyAnswer) return
    setSaveState("saving")
    try {
      const updated = await api.putHardConstraints(answerClarification(constraints, clarifyAnswer))
      setConstraints(updated)
      syncDrafts(updated)
      setSaveErrorCode(null)
      setSaveState("saved")
      setConfirmOpen(false)
      setDialogAck(false)
      setClarifyAnswer(null)
      setClarifyChoice(null)
      setClarifyLevel(null)
      startPlanSync({ fromKcal: null, toKcal: profile ? profile.calorieTarget.kcal : null })
    } catch (err) {
      setSaveErrorCode(err instanceof ApiError ? err.envelope.code : null)
      setSaveState("error")
    }
  }
  function answerFor(kind: "milk_or_lactose" | "lactose_level"): ClarificationAnswer | null {
    if (kind === "milk_or_lactose") {
      if (clarifyChoice === "allergy") return { kind, choice: "allergy" }
      if (clarifyChoice === "intolerance" && clarifyLevel)
        return { kind, choice: "intolerance", level: clarifyLevel }
      return null
    }
    return clarifyLevel ? { kind, level: clarifyLevel } : null
  }
  const levelOptions = [
    {
      value: "avoid_all_dairy",
      label: t("onboarding.step6.lactoseLevelAvoidAll"),
      sublabel: t("onboarding.step6.lactoseLevelAvoidAllSub"),
    },
    {
      value: "lactose_free_ok",
      label: t("onboarding.step6.lactoseLevelFreeOk"),
      sublabel: t("onboarding.step6.lactoseLevelFreeOkSub"),
    },
  ]

  if (state === "loading") return <LoadingState />
  if (state === "error" || !profile || !constraints)
    return <ErrorState onRetry={() => window.location.reload()} />

  return (
    <section
      aria-labelledby="constraints-heading"
      className="mx-auto flex w-full max-w-2xl flex-col gap-6"
    >
      <MoreSubpageHeader
        title={t("moreProfile.constraints.title")}
        headingId="constraints-heading"
      />

      <PlanSyncBanner
        state={planSync}
        onRetry={() =>
          startPlanSync({
            fromKcal: null,
            toKcal: profile ? profile.calorieTarget.kcal : null,
          })
        }
      />

      {/* ADR-080: a legacy "Milk / lactose" row waits for ONE non-blocking answer; the safe
          reading (milk allergy) holds until it is given. Answering goes through the same
          confirm dialog + regen as any hard-rule change. */}
      {constraints.clarifications.map(cl => (
        <div
          key={cl.kind}
          role="region"
          aria-labelledby={`clarify-${cl.kind}`}
          data-testid={`clarification-${cl.kind}`}
          className="flex flex-col gap-3 rounded-bb-xl border border-amber-300 bg-amber-50 p-4"
        >
          <h2 id={`clarify-${cl.kind}`} className="font-semibold text-ink">
            {t(
              cl.kind === "milk_or_lactose"
                ? "moreProfile.constraints.clarifyMilkTitle"
                : "moreProfile.constraints.clarifyLevelTitle"
            )}
          </h2>
          <p className="text-sm text-ink-2">
            {t(
              cl.kind === "milk_or_lactose"
                ? "moreProfile.constraints.clarifyMilkBody"
                : "moreProfile.constraints.clarifyLevelBody"
            )}
          </p>
          {cl.kind === "milk_or_lactose" && (
            <OptionGroup
              legend={t("moreProfile.constraints.clarifyMilkTitle")}
              options={[
                { value: "allergy", label: t("moreProfile.constraints.clarifyMilkAllergy") },
                {
                  value: "intolerance",
                  label: t("moreProfile.constraints.clarifyMilkIntolerance"),
                },
              ]}
              value={clarifyChoice}
              onChange={v => setClarifyChoice(v as "allergy" | "intolerance")}
              columns={2}
            />
          )}
          {(cl.kind === "lactose_level" || clarifyChoice === "intolerance") && (
            <OptionGroup
              legend={t("onboarding.step6.lactoseLevelTitle")}
              options={levelOptions}
              value={clarifyLevel}
              onChange={v => setClarifyLevel(v as LactoseLevel)}
            />
          )}
          <div>
            <Button
              type="button"
              className="min-h-11"
              disabled={answerFor(cl.kind) === null || saveState === "saving"}
              onClick={() => {
                const answer = answerFor(cl.kind)
                if (!answer) return
                setClarifyAnswer(answer)
                setConfirmOpen(true)
              }}
            >
              {t("moreProfile.constraints.clarifyConfirm")}
            </Button>
          </div>
        </div>
      ))}
      <div className="rounded-bb-xl border border-line bg-paper p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-ink">{t("moreProfile.constraints.allergensTitle")}</h2>
          {!editing && (
            <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)}>
              {t("moreProfile.constraints.editButton")}
            </Button>
          )}
        </div>

        {editing ? (
          <div className="mt-3 flex flex-col gap-4">
            <h3 className="font-semibold text-ink">
              {t("moreProfile.constraints.allergySectionTitle")}
            </h3>
            <ChipMultiSelect
              legend={t("moreProfile.constraints.allergySectionTitle")}
              items={[
                ...EU14.map(e => ({
                  value: e.value,
                  label: t(`onboarding.step6.allergens.${e.labelKey}`),
                })),
                { value: "intolerance:lactose", label: t("onboarding.step6.lactoseChip") },
              ]}
              value={mergeChipSelection(allergenDraft, lactoseDraft.selected)}
              onChange={next => {
                const split = splitChipSelection(next)
                setAllergenDraft(split.allergens === undefined ? "none" : split.allergens)
                setLactoseDraft(d => ({
                  selected: split.lactoseSelected,
                  level: split.lactoseSelected ? d.level : null,
                }))
              }}
              noneLabel={t("onboarding.step6.noneLabel")}
            />
            {lactoseDraft.selected && (
              <div
                className="flex flex-col gap-3 rounded-bb-md border border-line bg-cream/40 p-4"
                data-testid="lactose-level-card"
              >
                <p className="font-semibold text-ink">{t("onboarding.step6.lactoseLevelTitle")}</p>
                <OptionGroup
                  legend={t("onboarding.step6.lactoseLevelTitle")}
                  options={levelOptions}
                  value={lactoseDraft.level}
                  onChange={v => setLactoseDraft({ selected: true, level: v as LactoseLevel })}
                />
                <p className="text-xs text-ink-2">{t("onboarding.step6.lactoseLevelNote")}</p>
              </div>
            )}
            <FreeTextListOrNone
              inputId="constraints-intolerances"
              label={t("moreProfile.constraints.intoleranceTextLabel")}
              placeholder={t("onboarding.step6.intolerancesPlaceholder")}
              noneLabel={t("onboarding.step6.noneLabel")}
              value={intoleranceTextDraft}
              onChange={next => {
                const guarded = guardIntoleranceText(intoleranceTextDraft, next)
                setLactoseTextHint(guarded.rejected)
                setIntoleranceTextDraft(guarded.value === undefined ? "none" : guarded.value)
              }}
            />
            {lactoseTextHint && (
              <p
                role="note"
                className="rounded-bb-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                data-testid="lactose-text-hint"
              >
                {t("onboarding.step6.lactoseTextHint")}
              </p>
            )}
            <h3 className="font-semibold text-ink">
              {t("moreProfile.constraints.avoidSectionTitle")}
            </h3>
            <FreeTextListOrNone
              inputId="constraints-never-eat"
              label={t("onboarding.step7.neverEatLabel")}
              placeholder={t("onboarding.step7.neverEatPlaceholder")}
              noneLabel={t("onboarding.step7.noneButton")}
              value={neverEatDraft}
              onChange={setNeverEatDraft}
            />
            <div className="flex gap-2">
              <Button
                type="button"
                className="min-h-11"
                disabled={lactoseDraft.selected && !lactoseDraft.level}
                onClick={() => setConfirmOpen(true)}
              >
                {t("moreProfile.constraints.confirmButton")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="min-h-11"
                onClick={() => setEditing(false)}
              >
                {t("moreProfile.constraints.cancelButton")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-2 flex flex-col gap-3">
            {/* 2026-09-02 approved IA: safety allergies and chosen
                exclusions are the SAME stored constraints, presented as
                two clearly named groups — no data-model change. */}
            {(["allergen", "intolerance", "never_eat"] as const).map(type => (
              <div key={type}>
                <h3 className="text-xs font-bold tracking-wide text-ink-2 uppercase">
                  {type === "allergen"
                    ? t("moreProfile.constraints.allergySectionTitle")
                    : type === "intolerance"
                      ? t("moreProfile.constraints.intoleranceSectionTitle")
                      : t("moreProfile.constraints.avoidSectionTitle")}
                </h3>
                <ul className="mt-1.5 flex flex-wrap gap-2">
                  {constraints.constraints.filter(c => c.type === type).length === 0 && (
                    <li className="text-xs text-muted-foreground">
                      {t("moreProfile.constraints.noneChip")}
                    </li>
                  )}
                  {constraints.constraints
                    .filter(c => c.type === type)
                    .map(c => (
                      <li
                        key={`${c.type}-${c.subjectId}`}
                        className="rounded-full border border-line bg-cream px-3 py-1 text-xs text-ink"
                      >
                        {constraintChipLabel(c.type, c.subjectId, t, c.level)}
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {saveState === "saved" && (
          <p className="mt-2 text-sm text-green">{t("moreProfile.constraints.updated")}</p>
        )}
        {saveState === "error" && (
          <p className="mt-2 text-sm text-destructive">
            {t(
              saveErrorCode && SAVE_ERROR_COPY[saveErrorCode]
                ? SAVE_ERROR_COPY[saveErrorCode]
                : "moreProfile.constraints.updateError"
            )}
          </p>
        )}
      </div>

      {/* Owner request 2026-08-28: health conditions are now EDITABLE here.
          The write goes through the canonical health-condition path — the
          step-13 PUT (replaceHealthConditions + server-side target recompute,
          incl. the ADR-030 breastfeeding allowance) — so this edit behaves
          exactly like declaring during onboarding. ADR-030 safety mirrors:
          pregnancy + a loss goal blocks the save (change the goal first);
          breastfeeding + a loss goal requires the same acknowledgement. */}
      <div className="rounded-bb-xl border border-line bg-paper p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-ink">{t("moreProfile.constraints.healthTitle")}</h2>
          {!healthEditing && (
            <Button type="button" variant="outline" onClick={() => startHealthEdit()}>
              {t("moreProfile.constraints.editButton")}
            </Button>
          )}
        </div>

        {!healthEditing ? (
          profile.healthConditions.length === 0 ? (
            <p className="mt-2 text-sm text-ink-2">{t("onboarding.step13.noneLabel")}</p>
          ) : (
            <ul className="mt-2 flex flex-wrap gap-2">
              {profile.healthConditions.map(c => (
                <li
                  key={c.condition}
                  className="rounded-full border border-line bg-cream px-3 py-1 text-xs text-ink"
                >
                  {t(`onboarding.step13.conditions.${c.condition}`)}
                </li>
              ))}
            </ul>
          )
        ) : (
          <div className="mt-3 flex flex-col gap-3">
            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-label={t("moreProfile.constraints.healthTitle")}
            >
              {HEALTH_CONDITION_VALUES.map(condition => {
                const active = healthDraft.includes(condition)
                return (
                  <button
                    key={condition}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleHealthCondition(condition)}
                    className={`min-h-11 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                      active
                        ? "border-green bg-green/10 text-ink"
                        : "border-line bg-paper text-ink hover:border-green/60"
                    }`}
                  >
                    {t(`onboarding.step13.conditions.${condition}`)}
                  </button>
                )
              })}
            </div>

            {healthDraft.includes("other") && (
              <input
                aria-label={t("onboarding.step13.otherLabel")}
                placeholder={t("onboarding.step13.otherPlaceholder")}
                value={healthOtherText}
                onChange={e => setHealthOtherText(e.target.value)}
                className="min-h-11 rounded-bb-md border border-line bg-paper px-3 text-sm text-ink"
              />
            )}

            {healthDraft.length > 0 && (
              <FreeTextListOrNone
                inputId="constraints-medications"
                label={t("onboarding.step13.medicationsLabel")}
                placeholder={t("onboarding.step13.medicationsPlaceholder")}
                noneLabel={t("onboarding.step13.noneLabel")}
                required={false}
                value={healthMeds.length === 0 ? "none" : healthMeds}
                onChange={next => setHealthMeds(next === "none" ? [] : next)}
              />
            )}

            {pregnancyLossBlocked && (
              <p
                role="alert"
                className="rounded-bb-md border border-coral/50 bg-coral/10 p-3 text-sm text-ink"
              >
                {t("onboarding.correction.pregnancyGoalBody")}
              </p>
            )}

            {breastfeedingLossAckNeeded && (
              <label className="flex items-start gap-2 rounded-bb-md border border-line bg-cream p-3 text-sm text-ink">
                <Checkbox
                  id="health-bf-ack"
                  checked={healthBfAck}
                  onCheckedChange={setHealthBfAck}
                />
                <span>{t("onboarding.correction.acknowledgeBreastfeedingLossLabel")}</span>
              </label>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                className="min-h-11"
                disabled={
                  healthSaveState === "saving" ||
                  pregnancyLossBlocked ||
                  (breastfeedingLossAckNeeded && !healthBfAck) ||
                  (healthDraft.includes("other") && healthOtherText.trim() === "")
                }
                onClick={() => void handleHealthSave()}
              >
                {healthSaveState === "saving"
                  ? t("onboarding.shell.saving")
                  : t("moreProfile.constraints.confirmButton")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="min-h-11"
                onClick={() => setHealthEditing(false)}
              >
                {t("moreProfile.constraints.cancelButton")}
              </Button>
              {healthSaveState === "saved" && (
                <span className="text-sm text-green">{t("moreProfile.profile.saved")}</span>
              )}
              {healthSaveState === "error" && (
                <span className="text-sm text-destructive">
                  {t("moreProfile.profile.saveError")}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("moreProfile.constraints.dialogTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("moreProfile.constraints.dialogBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-start gap-2 text-sm text-ink">
            <Checkbox id="constraints-ack" checked={dialogAck} onCheckedChange={setDialogAck} />
            <span>{t("moreProfile.constraints.dialogConfirmLabel")}</span>
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setDialogAck(false)
                setClarifyAnswer(null)
              }}
            >
              {t("moreProfile.constraints.cancelButton")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={!dialogAck || saveState === "saving"}
              onClick={() => void (clarifyAnswer ? submitClarification() : handleConfirm())}
            >
              {t("moreProfile.constraints.confirmButton")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
